import { Link } from "react-router-dom";
import { useBeforeAfterPairs } from "@/lib/beforeAfterPairs";
import BeforeAfterSlider from "@/components/BeforeAfterSlider";
import { useSiteContent, isEnabled } from "@/lib/siteContent";
import { resolveMediaUrl, mediaProxyUrlForced, type MediaRef } from "@/lib/media";
import { useMediaAiFlags } from "@/lib/aiFlags";

type Props = {
    source: "main" | "sylt";
    /** Dark-Section (weißer Text auf dunklem Grund) statt hell */
    dark?: boolean;
};

type CmsPair = { id?: string; title?: string; vorher?: MediaRef; nachher?: MediaRef };
interface BASection {
    eyebrow?: string; headline?: string; lead?: string;
    pairs?: CmsPair[];
}

/**
 * URL-Helper: liefert für ein MediaRef die Same-Origin-Proxy-URL wenn
 * `media_id` vorhanden ist (CORS-safe, weil das `<img>` mit crossOrigin
 * gearbeitet hat und R2-dev keine ACAO-Header sendet). Sonst Fallback auf
 * resolveMediaUrl (statische Pfade / absolute URLs).
 */
function sliderUrl(ref: MediaRef): string {
    if (ref && typeof ref === "object" && ref.media_id) {
        return mediaProxyUrlForced(ref.media_id);
    }
    return resolveMediaUrl(ref, "");
}

function refMediaId(ref: MediaRef | undefined): string | undefined {
    if (ref && typeof ref === "object" && "media_id" in ref && typeof ref.media_id === "string") {
        return ref.media_id;
    }
    return undefined;
}

/**
 * Sektion „Vorher · Nachher" für Startseite und /sylt.
 *
 * Phase-2b: Primär aus CMS `home.before_after.pairs` bzw. `sylt.before_after.pairs`.
 * Fallback auf die alte Projekte-Ansicht via `/api/pairs/{source}`, wenn die
 * CMS-Section leer/nicht vorhanden ist.
 */
export default function BeforeAfterSection({ source, dark = false }: Props) {
    const { getObject } = useSiteContent();
    const cmsKey = source === "sylt" ? "sylt.before_after" : "home.before_after";
    const cms = getObject<BASection>(cmsKey, {});
    const cmsPairs = (cms.pairs || []).filter(isEnabled).filter((p) => p.vorher && p.nachher);

    const { items, loading } = useBeforeAfterPairs(source);

    // Sammle alle Media-IDs beider Pfade (CMS + Legacy-API), damit wir
    // pro Bild das `is_ai_generated`-Flag aus `/api/media/{id}/info`
    // ziehen können. Cache liegt im Hook — kein redundanter Fetch.
    const mediaIds: Array<string | undefined> = [
        ...cmsPairs.flatMap((p) => [refMediaId(p.vorher), refMediaId(p.nachher)]),
        ...items.flatMap((p) => [p.before?.id, p.after?.id]),
    ];
    const aiFlags = useMediaAiFlags(mediaIds);

    // CMS-Pairs haben Vorrang. Wenn keine → API-Pairs als Fallback.
    const useCms = cmsPairs.length > 0;
    if (!useCms && loading) return null;
    if (!useCms && !items.length) return null;

    return (
        <section
            className="py-20 md:py-28"
            style={dark ? { background: "hsl(var(--hf-ink))", color: "hsl(var(--hf-bg))" } : undefined}
            data-testid={`before-after-section-${source}`}
        >
            <div className="max-w-6xl mx-auto px-6">
                <div className="max-w-3xl mb-10 md:mb-14">
                    <div className="hf-eyebrow" style={dark ? { color: "hsl(var(--hf-gold-soft))" } : undefined}>
                        {cms.eyebrow || "Vergleich"}
                    </div>
                    <h2 className="mt-3 font-display text-3xl sm:text-4xl md:text-5xl leading-[1.1]">
                        {cms.headline ? (
                            cms.headline
                        ) : (
                            <>
                                Vorher{" "}
                                <em
                                    className="not-italic"
                                    style={{
                                        color: dark ? "hsl(var(--hf-gold-soft))" : "hsl(var(--hf-gold-dark))",
                                        fontStyle: "italic",
                                    }}
                                >
                                    · Nachher.
                                </em>
                            </>
                        )}
                    </h2>
                    <p
                        className="mt-4 text-base md:text-lg leading-relaxed max-w-2xl"
                        style={dark ? { color: "hsl(var(--hf-bg) / 0.85)" } : { color: "hsl(var(--hf-muted))" }}
                    >
                        {cms.lead || "Ziehen Sie den Regler, um den Zustand vor und nach der Dokumentation direkt gegenüberzustellen."}
                    </p>
                </div>
                <div className="mx-auto max-w-7xl px-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                    {/*
                      * 5-Paar-Layout: 3 Spalten auf lg (Desktop → 3+2), 2
                      * auf md, 1 auf mobile (vertikaler Stack). Kacheln
                      * bekommen abgerundete Ecken + Schatten für edleren
                      * Look. `max-w-[520px]` pro Kachel begrenzt die Breite
                      * damit der Slider auch bei weniger Kacheln nicht
                      * gigantisch groß wird.
                      */}
                    {useCms
                        ? cmsPairs.map((p, i) => (
                            <figure
                                key={p.id || i}
                                data-testid={`ba-pair-cms-${i}`}
                                className="max-w-[520px] w-full mx-auto rounded-xl overflow-hidden shadow-lg"
                            >
                                <BeforeAfterSlider
                                    beforeUrl={sliderUrl(p.vorher)}
                                    afterUrl={sliderUrl(p.nachher)}
                                    beforeAlt={p.title ? `${p.title} · Vorher` : "Vorher"}
                                    afterAlt={p.title ? `${p.title} · Nachher` : "Nachher"}
                                    beforeAi={!!aiFlags[refMediaId(p.vorher) || ""]}
                                    afterAi={!!aiFlags[refMediaId(p.nachher) || ""]}
                                />
                                {p.title && (
                                    <figcaption
                                        className="mt-3 font-display text-lg leading-tight"
                                        style={dark ? { color: "hsl(var(--hf-bg))" } : undefined}
                                    >
                                        {p.title}
                                    </figcaption>
                                )}
                            </figure>
                        ))
                        : items.map((pair) => (
                            <figure
                                key={pair.pair_id}
                                data-testid={`ba-pair-${pair.pair_id}`}
                                className="max-w-[520px] w-full mx-auto rounded-xl overflow-hidden shadow-lg"
                            >
                                <BeforeAfterSlider
                                    beforeUrl={pair.before.url as string}
                                    afterUrl={pair.after.url as string}
                                    beforeAlt={pair.before.alt || "Vorher"}
                                    afterAlt={pair.after.alt || "Nachher"}
                                    beforeAi={!!aiFlags[pair.before.id]}
                                    afterAi={!!aiFlags[pair.after.id]}
                                />
                                <figcaption className="mt-3 flex items-baseline justify-between gap-3">
                                    <Link
                                        to={`/projekt/${pair.project.slug}`}
                                        className="font-display text-lg leading-tight hover:underline"
                                        style={dark ? { color: "hsl(var(--hf-bg))" } : undefined}
                                    >
                                        {pair.project.title}
                                    </Link>
                                    {pair.project.location && (
                                        <span
                                            className="text-[11px] tracking-[0.16em] uppercase"
                                            style={{ color: dark ? "hsl(var(--hf-gold-soft))" : "hsl(var(--hf-muted))" }}
                                        >
                                            {pair.project.location}
                                        </span>
                                    )}
                                </figcaption>
                            </figure>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}
